import * as ipaddr from 'ipaddr.js';

export class IpHelper {
  /**
   * Converts an IPv4 address string to a numeric representation.
   * @param ipStr IPv4 address in string format.
   */
  private static ipToLong(ipStr: string): number {
    return (
      ipaddr
        .parse(ipStr)
        .toByteArray()
        .reduce((acc, byte) => (acc << 8) + byte, 0) >>> 0
    );
  }

  /**
   * Converts a numeric representation of an IPv4 address back to a string.
   * @param longIp Numeric representation of an IPv4 address.
   */
  private static longToIp(longIp: number): string {
    return [(longIp >>> 24) & 255, (longIp >>> 16) & 255, (longIp >>> 8) & 255, longIp & 255].join('.');
  }

  /**
   * Expands an IP range into an array of individual IPs.
   * @param rangeStr IP range in "startIP-endIP" format.
   */
  static expandIpRange(rangeStr: string): string[] {
    const [startIp, endIp] = rangeStr.split('-');
    if (!ipaddr.IPv4.isValid(startIp) || !ipaddr.IPv4.isValid(endIp)) {
      throw new Error(`Invalid IP range format: ${rangeStr}`);
    }

    const startLong = this.ipToLong(startIp);
    const endLong = this.ipToLong(endIp);

    if (startLong > endLong) {
      throw new Error(`Invalid IP range: Start IP ${startIp} is greater than End IP ${endIp}`);
    }

    const expandedIps: string[] = [];
    for (let currentIp = startLong; currentIp <= endLong; currentIp++) {
      expandedIps.push(this.longToIp(currentIp));
    }
    return expandedIps;
  }

  /**
   * Expands an IP/CIDR into an array of individual IPs.
   * @param cidrStr IP in CIDR notation.
   */
  static expandCidr(cidrStr: string): string[] {
    const [ipStr, prefix] = cidrStr.split('/');
    if (!ipaddr.IPv4.isValid(ipStr) || isNaN(parseInt(prefix))) {
      throw new Error(`Invalid CIDR format: ${cidrStr}`);
    }

    const network = ipaddr.IPv4.parse(ipStr);
    const maskBits = parseInt(prefix);
    const subnetSize = Math.pow(2, 32 - maskBits);
    const startLong = this.ipToLong(network.toString()) & (~(subnetSize - 1) >>> 0);
    const endLong = startLong + subnetSize - 1;

    const expandedIps: string[] = [];
    for (let currentIp = startLong; currentIp <= endLong; currentIp++) {
      expandedIps.push(this.longToIp(currentIp));
    }

    return expandedIps;
  }

  /**
   * Expands a mixed format IP entry into individual IPs.
   * Supports: single IPs, CIDR subnets, and IP ranges.
   * @param entry Network entry.
   */
  static expandIpEntry(entry: string): string[] {
    if (entry.includes('-')) {
      return this.expandIpRange(entry);
    } else if (entry.includes('/')) {
      return this.expandCidr(entry);
    } else if (ipaddr.IPv4.isValid(entry)) {
      return [entry]; // Single IP
    } else {
      throw new Error(`Invalid IP format: ${entry}`);
    }
  }

  /**
   * Validates that exclude_networks is a subset of networks.
   * @param networks Included networks as string[].
   * @param excludeNetworks Excluded networks as string[].
   */
  static hasIpExcludeError(networks: string[], excludeNetworks: string[]): any {
    const includedIps = new Set<string>();
    const excludedIps = new Set<string>();

    // Expand all network IPs into a Set.
    networks.forEach((net) => {
      this.expandIpEntry(net).forEach((ip) => includedIps.add(ip));
    });

    // Expand all exclude_networks into a Set.
    excludeNetworks.forEach((net) => {
      this.expandIpEntry(net).forEach((ip) => excludedIps.add(ip));
    });

    // Ensure every excluded IP exists in the included set.
    for (const ip of excludedIps) {
      if (!includedIps.has(ip)) {
        console.error(`Invalid Exclusion: ${ip} is not part of the included networks.`);
        return { message: `Invalid Exclusion: ${ip} is not part of the included networks.` };
      }
    }
    return null;
  }
}
